// src/screens/HudFetcher.tsx
// HUD HomeStore Fetcher — Redfin-style layout with 5 data-backed tabs.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Alert,
  LinearProgress,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import { apiFetch } from '../helpers';

interface HudProp {
  _id: string;
  address: string;
  fullAddress: string;
  city: string | null;
  state: string;
  zip: string | null;
  price: number | null;
  scrapedAt: string;
  // joined from ScrapedDeal
  joinedAmv?: number | null;
  bofaAmv?: number | null;
  amvSource?: string | null;
  isDeal?: boolean;
  dealStage?: string | null;
  bofaStatus?: string | null;
}

interface StatusResponse {
  ok: boolean;
  isRunning: boolean;
  progress: {
    statesCompleted: number;
    totalStates: number;
    totalProperties: number;
    currentState: string | null;
  };
  totalInDb: number;
  byState: { _id: string; count: number }[];
  cycle?: {
    cycleCount: number;
    lastCycleStartedAt: string | null;
    lastCycleCompletedAt: string | null;
    lastCycleFullyCompleted: boolean;
    consecutiveStateFailuresAllTime: number;
  };
}

interface TabCounts {
  total: number;
  withAmv: number;
  bofaQueue: number;
  redfinQueue: number;
  withoutAmv: number;
  deals: number;
}

type ViewMode = 'all' | 'with_amv' | 'bofa_queue' | 'redfin_queue' | 'without_amv';

const currency = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmt = (n?: number | null) => (typeof n === 'number' && n > 0 ? currency.format(n) : '—');

export default function HudFetcher() {
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [counts, setCounts] = useState<TabCounts>({ total: 0, withAmv: 0, bofaQueue: 0, redfinQueue: 0, withoutAmv: 0, deals: 0 });
  const [rows, setRows] = useState<HudProp[]>([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>('all');
  const [filterState, setFilterState] = useState('all');
  const [loading, setLoading] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiFetch('/api/hud-scrape/status');
      const data = await res.json();
      setStatus(data);
    } catch (err: any) {
      setError(err.message);
    }
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const res = await apiFetch('/api/hud-scrape/compare');
      const data = await res.json();
      if (data.ok) setCounts(data.counts);
    } catch (err: any) {
      setError(err.message);
    }
  }, []);

  const fetchRows = useCallback(async () => {
    setRowsLoading(true);
    try {
      const stateParam = filterState !== 'all' ? `&state=${filterState}` : '';
      const res = await apiFetch(`/api/hud-scrape/properties?viewMode=${viewMode}&limit=200${stateParam}`);
      const data = await res.json();
      if (data.ok) {
        setRows(data.properties);
        setRowsTotal(data.total);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setRowsLoading(false);
    }
  }, [viewMode, filterState]);

  useEffect(() => {
    fetchStatus();
    fetchCounts();
    const interval = setInterval(() => { fetchStatus(); fetchCounts(); }, 5000);
    return () => clearInterval(interval);
  }, [fetchStatus, fetchCounts]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleStart = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await apiFetch('/api/hud-scrape/start', { method: 'POST', body: JSON.stringify({}) });
      const data = await res.json();
      if (data.ok) setMessage(`Scrape started for ${data.states.length} states`);
      else setError(data.error || 'Failed to start');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    try {
      const res = await apiFetch('/api/hud-scrape/stop', { method: 'POST' });
      const data = await res.json();
      setMessage(data.message);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSendToAmv = async () => {
    setSending(true);
    setError('');
    setMessage('');
    try {
      const res = await apiFetch('/api/hud-scrape/send-to-amv', { method: 'POST' });
      const data = await res.json();
      if (data.ok) {
        setMessage(`Sent ${data.sent} to AMV pipeline (${data.skipped} already existed)`);
        fetchCounts();
        fetchRows();
      } else {
        setError(data.error || 'Failed to send');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const isRunning = status?.isRunning || false;
  const progress = status?.progress;
  const pct = progress && progress.totalStates > 0
    ? Math.round((progress.statesCompleted / progress.totalStates) * 100)
    : 0;
  const cycle = status?.cycle;

  const fmtDate = (iso?: string | null) => iso ? new Date(iso).toLocaleString() : '—';

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#111' }}>
          HUD Properties
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {/* success banner disabled — no toast on Start/Stop/Send actions
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      */}

      {/* Stat cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Total</Typography>
          <Typography variant="h5" fontWeight={700}>{counts.total.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">With AMV</Typography>
          <Typography variant="h5" fontWeight={700} color="success.main">{counts.withAmv.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">BofA Queue</Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#2563eb' }}>{counts.bofaQueue.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Redfin Queue</Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#dc2626' }}>{counts.redfinQueue.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Without AMV</Typography>
          <Typography variant="h5" fontWeight={700} color="warning.main">{counts.withoutAmv.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Deals</Typography>
          <Typography variant="h5" fontWeight={700} color="primary.main">{counts.deals.toLocaleString()}</Typography>
        </Paper>
        {cycle && (
          <Paper variant="outlined" sx={{ p: 2, minWidth: 160 }}>
            <Typography variant="caption" color="text.secondary">Cycle</Typography>
            <Typography variant="h5" fontWeight={700}>#{cycle.cycleCount}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {cycle.lastCycleFullyCompleted ? 'Full cycle' : cycle.lastCycleCompletedAt ? 'Partial' : 'Running...'}
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Controls */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          {!isRunning ? (
            <Button variant="contained" color="success" startIcon={<PlayArrowIcon />} onClick={handleStart} disabled={loading} sx={{ fontWeight: 600 }}>
              Start Scrape
            </Button>
          ) : (
            <Button variant="contained" color="error" startIcon={<StopIcon />} onClick={handleStop} sx={{ fontWeight: 600 }}>
              Stop
            </Button>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { fetchStatus(); fetchCounts(); fetchRows(); }}>
            Refresh
          </Button>
          <Button variant="outlined" color="info" startIcon={<SendIcon />} onClick={handleSendToAmv} disabled={sending}>
            {sending ? 'Sending...' : 'Send Pending → AMV'}
          </Button>
          {cycle && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              Last cycle: {fmtDate(cycle.lastCycleCompletedAt)} · Started: {fmtDate(cycle.lastCycleStartedAt)}
            </Typography>
          )}
        </Stack>

        {isRunning && progress && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {progress.statesCompleted}/{progress.totalStates} states — {progress.totalProperties} saved
              </Typography>
              {progress.currentState && (
                <Chip label={`Scraping: ${progress.currentState}`} color="info" size="small" />
              )}
            </Stack>
            <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3 }} />
          </Box>
        )}
      </Paper>

      {/* Tabs + state filter */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
        <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small">
          <ToggleButton value="all" sx={{ textTransform: 'none', fontWeight: 700 }}>
            All ({counts.total.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="with_amv" sx={{ textTransform: 'none', fontWeight: 600 }}>
            With AMV ({counts.withAmv.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="bofa_queue" sx={{ textTransform: 'none', fontWeight: 600 }}>
            BofA Queue ({counts.bofaQueue.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="redfin_queue" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Redfin Queue ({counts.redfinQueue.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="without_amv" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Without AMV ({counts.withoutAmv.toLocaleString()})
          </ToggleButton>
        </ToggleButtonGroup>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>State</InputLabel>
          <Select value={filterState} label="State" onChange={(e) => setFilterState(e.target.value)}>
            <MenuItem value="all">All States</MenuItem>
            {(status?.byState || []).map((s) => (
              <MenuItem key={s._id} value={s._id}>{s._id} ({s.count})</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Native dark-header table (Redfin-style) */}
      <div
        style={{
          overflowX: 'auto',
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, color: '#111' }}>
            {viewMode === 'all' && `All Properties (${counts.total.toLocaleString()})`}
            {viewMode === 'with_amv' && `With AMV (${counts.withAmv.toLocaleString()})`}
            {viewMode === 'bofa_queue' && `BofA Queue (${counts.bofaQueue.toLocaleString()})`}
            {viewMode === 'redfin_queue' && `Redfin Queue (${counts.redfinQueue.toLocaleString()})`}
            {viewMode === 'without_amv' && `Without AMV (${counts.withoutAmv.toLocaleString()})`}
          </span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            Showing {rows.length}{rowsTotal > rows.length ? ` of ${rowsTotal.toLocaleString()}` : ''} rows
          </span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr style={{ background: '#111827', color: '#fff' }}>
              {['Address', 'State', 'List Price', 'AMV', 'Source', 'Deal?', 'Status'].map((h, i) => (
                <th
                  key={h}
                  style={{
                    textAlign: i === 0 ? 'left' : 'right',
                    padding: '12px 14px',
                    fontSize: 12,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowsLoading && (
              <tr>
                <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                  <CircularProgress size={18} sx={{ mr: 1 }} /> Loading...
                </td>
              </tr>
            )}
            {!rowsLoading && rows.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                  {viewMode === 'all' && 'No properties yet. Click "Start Scrape" to fetch from HUD.'}
                  {viewMode === 'with_amv' && 'No properties with AMV yet.'}
                  {viewMode === 'bofa_queue' && 'BofA queue is empty.'}
                  {viewMode === 'redfin_queue' && 'Redfin queue is empty — nothing waiting for Redfin fallback.'}
                  {viewMode === 'without_amv' && 'Nothing here — every property got an AMV from BofA or Redfin.'}
                </td>
              </tr>
            )}
            {!rowsLoading && rows.map((p, i) => {
              const zebra = i % 2 === 0 ? '#ffffff' : '#f9fafb';
              const bg = p.isDeal ? '#f0fdf4' : zebra;
              const amv = p.joinedAmv || p.bofaAmv || 0;
              const hasAmv = amv > 0;
              return (
                <tr key={p._id} style={{ background: bg }}>
                  <td style={{ padding: 14, minWidth: 280, color: '#111', fontWeight: 600 }}>
                    {p.fullAddress || '—'}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right', color: '#6b7280' }}>{p.state || '—'}</td>
                  <td style={{ padding: 14, textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(p.price)}</td>
                  <td style={{ padding: 14, textAlign: 'right', color: hasAmv ? '#7c3aed' : '#9ca3af', fontWeight: 600 }}>
                    {hasAmv ? fmt(amv) : '—'}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right' }}>
                    {p.amvSource ? (
                      <Chip
                        label={p.amvSource}
                        size="small"
                        sx={{
                          fontSize: 10,
                          fontWeight: 600,
                          bgcolor: p.amvSource === 'bofa' ? '#ede9fe' : p.amvSource === 'redfin' ? '#dcfce7' : '#dbeafe',
                          color: p.amvSource === 'bofa' ? '#6d28d9' : p.amvSource === 'redfin' ? '#166534' : '#1d4ed8',
                        }}
                      />
                    ) : (
                      <span style={{ color: '#9ca3af' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right' }}>
                    {p.isDeal ? (
                      <Chip label="DEAL" size="small" sx={{ bgcolor: '#22c55e', color: '#fff', fontWeight: 600, fontSize: 10 }} />
                    ) : (
                      <span style={{ color: '#9ca3af' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right' }}>
                    <Chip
                      label={p.bofaStatus || p.dealStage || (hasAmv ? 'done' : 'pending')}
                      size="small"
                      sx={{
                        fontSize: 10,
                        fontWeight: 600,
                        bgcolor:
                          p.bofaStatus === 'success' ? '#dcfce7' :
                          p.bofaStatus === 'no_data' ? '#fef3c7' :
                          p.bofaStatus === 'failed' ? '#fee2e2' :
                          p.dealStage === 'bofa_amv_pending' ? '#dbeafe' : '#f3f4f6',
                        color:
                          p.bofaStatus === 'success' ? '#166534' :
                          p.bofaStatus === 'no_data' ? '#92400e' :
                          p.bofaStatus === 'failed' ? '#991b1b' :
                          p.dealStage === 'bofa_amv_pending' ? '#1d4ed8' : '#6b7280',
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Box>
  );
}
